// Sizing rules for the draggable customer side panel (Answers / Team Assist /
// Task). The stored preference is deliberately kept unclamped — it syncs across
// devices — so every bound is applied here, against the window in use.

/**
 * @param {number} width stored preference in px; 0 when the agent has never dragged
 * @param {number} containerWidth px available to the whole customer row; 0 before measurement
 */
export function clampSidePanelWidth(width, containerWidth) {
  if (!width) {
    return bound(getDefaultSidePanelWidth(containerWidth), MIN_DEFAULT_SIDE_PANEL_WIDTH, containerWidth);
  }
  return bound(width, MIN_SIDE_PANEL_WIDTH, containerWidth);
}

/**
 * Width for an agent who has never dragged the handle: the proportional rule the
 * panel used before it was resizable, so shipping drag-to-resize doesn't
 * re-lay-out anyone who never asked for it.
 */
export function getDefaultSidePanelWidth(containerWidth) {
  if (!containerWidth) {
    return DEFAULT_SIDE_PANEL_WIDTH;
  }
  return Math.min(DEFAULT_SIDE_PANEL_WIDTH, containerWidth * DEFAULT_SIDE_PANEL_FRACTION);
}

/**
 * The widest the panel may go right now. MIN wins over the content reservation,
 * so a narrow window stops the panel growing rather than collapsing it.
 */
export function getMaxSidePanelWidth(containerWidth) {
  if (!containerWidth) {
    return MAX_SIDE_PANEL_WIDTH;
  }
  return Math.max(MIN_SIDE_PANEL_WIDTH, Math.min(MAX_SIDE_PANEL_WIDTH, containerWidth - MIN_CONTENT_WIDTH));
}

export function isSidePanelResizable(containerWidth) {
  return getMaxSidePanelWidth(containerWidth) > MIN_SIDE_PANEL_WIDTH;
}

function bound(width, minWidth, containerWidth) {
  return Math.round(Math.min(Math.max(width, minWidth), getMaxSidePanelWidth(containerWidth)));
}

// The pre-resize styling was `flex-basis: 25%; max-width: 360px; min-width: 200px`.
export const DEFAULT_SIDE_PANEL_WIDTH = 360;
export const DEFAULT_SIDE_PANEL_FRACTION = 0.25;
export const MIN_DEFAULT_SIDE_PANEL_WIDTH = 200;

// Floor for a width the agent chose, which sits above MIN_DEFAULT_SIDE_PANEL_WIDTH
// on purpose: a dragged panel narrower than this is unusable, but an inherited
// one must stay wherever the old rule put it.
export const MIN_SIDE_PANEL_WIDTH = 280;
export const MAX_SIDE_PANEL_WIDTH = 720;

// Reserved for the column left of the panel. Covers the whole column including
// the fixed-width customer summary, so the conversation feed gets rather less.
export const MIN_CONTENT_WIDTH = 640;

export const SIDE_PANEL_WIDTH_STEP = 16;
